import { useState } from 'react';
import styled from '@emotion/styled';

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #2d3748;
  margin-bottom: 2rem;
  text-align: center;
`;

const Description = styled.p`
  color: #4a5568;
  text-align: center;
  font-size: 1.1rem;
  margin-bottom: 3rem;
  line-height: 1.6;
`;

const Form = styled.form`
  max-width: 600px;
  margin: 0 auto;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: #4a5568;
  font-size: 1rem;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 1rem;
  color: #2d3748;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #4299e1;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 1rem;
  color: #2d3748;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #4299e1;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  font-size: 1rem;
  color: #2d3748;
  background-color: white;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #4299e1;
  }
`;

const SubmitButton = styled.button`
  background-color: #48bb78;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #38a169;
  }

  &:disabled {
    background-color: #9ae6b4;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #e53e3e;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SuccessMessage = styled.div`
  color: #38a169;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  text-align: center;
`;

const Required = styled.span`
  color: #e53e3e;
  font-size: 0.875rem;
  margin-left: 0.25rem;
`;

const RadioGroup = styled.div`
  margin: 0.5rem 0;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  color: #4a5568;
  font-size: 1rem;
  margin-bottom: 0.5rem;
  cursor: pointer;

  input {
    margin-right: 0.5rem;
  }
`;

const HelpText = styled.p`
  color: #718096;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

interface FormData {
  name: string;
  email: string;
  app: string;
  device: string;
  userId: string;
  message: string;
}

const Support = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    app: '',
    device: '',
    userId: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch('https://ggtude.com/wp-json/api/v1/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to send message');
      }

      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        app: '',
        device: '',
        userId: '',
        message: ''
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send the message. Please try again later.');
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Title>Support</Title>
      <Description>
        If you have questions, this is the right place. Please fill in the details and we'll do our best to get back to you as soon as we can.
      </Description>

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="name">Name <Required>*</Required></Label>
          <Input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="email">Email <Required>*</Required></Label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>App <Required>*</Required></Label>
          <RadioGroup>
            <RadioLabel>
              <input
                type="radio"
                name="app"
                value="OCD.app – Anxiety Mood & Sleep"
                checked={formData.app === "OCD.app – Anxiety Mood & Sleep"}
                onChange={handleInputChange}
                required
              />
              OCD.app – Anxiety Mood & Sleep
            </RadioLabel>
            <RadioLabel>
              <input
                type="radio"
                name="app"
                value="Thinkable Health"
                checked={formData.app === "Thinkable Health"}
                onChange={handleInputChange}
                required
              />
              Thinkable Health
            </RadioLabel>
          </RadioGroup>
        </FormGroup>

        <FormGroup>
          <Label>Your device <Required>*</Required></Label>
          <RadioGroup>
            <RadioLabel>
              <input
                type="radio"
                name="device"
                value="iPhone / iPad"
                checked={formData.device === "iPhone / iPad"}
                onChange={handleInputChange}
                required
              />
              iPhone / iPad
            </RadioLabel>
            <RadioLabel>
              <input
                type="radio"
                name="device"
                value="Android"
                checked={formData.device === "Android"}
                onChange={handleInputChange}
                required
              />
              Android
            </RadioLabel>
            <RadioLabel>
              <input
                type="radio"
                name="device"
                value="Other"
                checked={formData.device === "Other"}
                onChange={handleInputChange}
                required
              />
              Other
            </RadioLabel>
          </RadioGroup>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="userId">User ID</Label>
          <Input
            type="text"
            id="userId"
            name="userId"
            value={formData.userId}
            onChange={handleInputChange}
          />
          <HelpText>Optional – used to help us fix problems with the app</HelpText>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="message">Your message in detail <Required>*</Required></Label>
          <TextArea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            required
          />
        </FormGroup>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>Thank you for your message. We'll get back to you soon!</SuccessMessage>}

        <SubmitButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Sending...' : 'Submit'}
        </SubmitButton>
      </Form>
    </Container>
  );
};

export default Support; 